/**
 * Character builder. Layered: body shape, head/hat, face, body hue,
 * accent hue. Lives preview lives in the parent (CharacterCreate).
 *
 * Two variants (NPE-3, docs/planning/NPE-Redesign.md §D):
 * - 'edit' (default; Locker / profile modal): the full catalog, locked
 *   items visible + inspectable — browsing what's earnable is the point.
 * - 'create' (the new-player creator): locked items COLLAPSE into a
 *   one-line teaser per section so a first-timer sees real choices, not
 *   a wall of padlocks. Sections are chip-navigable.
 */
import { useRef } from 'react';
import { IconAvatar, type IconValue } from './IconAvatar';
import { Character3DThumbnail } from './Character3DThumbnail';
import { BODY_SHAPES, HAT_SHAPES, FACE_SHAPES, HUES } from './characterParts';
import type { CosmeticInventory } from '../../lib/api';

/** Body archetypes (Phase 2) — drawn by the Pixi renderer at idx 100+.
 *  The IconAvatar SVG can't render these yet, so the picker shows a
 *  small palette swatch as a placeholder. The id matches drawBody. */
const BODY_ARCHETYPES: Array<{ id: number; label: string; color: string; rarity: 3 | 4 }> = [
  { id: 100, label: 'Floating Orb', color: 'hsl(28 60% 56%)',  rarity: 3 },
  { id: 101, label: 'Eight-Legged', color: 'hsl(290 50% 48%)', rarity: 3 },
  { id: 102, label: 'Phantom',      color: 'hsl(220 30% 70%)', rarity: 4 },
  { id: 103, label: 'Crystal',      color: 'hsl(180 45% 60%)', rarity: 4 },
  { id: 104, label: 'Living Flame', color: 'hsl(20 80% 60%)',  rarity: 4 },
  { id: 105, label: 'Hexagon',      color: 'hsl(140 50% 50%)', rarity: 4 },
];

/** 3-color palette catalog. Mirrors PALETTES in shared/character-render
 *  but rendered as gradient swatches for the picker. */
const PALETTE_SWATCHES: Array<{ id: string; label: string; gradient: string; rarity: 2 | 3 | 4 }> = [
  { id: 'palette-dusk',      label: 'Dusk',      gradient: 'linear-gradient(135deg,#3b1452 0%,#a05078 50%,#dec07a 100%)',   rarity: 2 },
  { id: 'palette-bone',      label: 'Bone',      gradient: 'linear-gradient(135deg,#e6dfd0 0%,#88858f 50%,#3a3742 100%)',   rarity: 3 },
  { id: 'palette-emerald',   label: 'Emerald',   gradient: 'linear-gradient(135deg,#176e3e 0%,#e2c33b 60%,#cae6c2 100%)',   rarity: 3 },
  { id: 'palette-prismatic', label: 'Prismatic', gradient: 'conic-gradient(from 0deg, #ff4a2a, #e2c33b, #6abf3f, #4a9eff, #9b6dff, #ff4a8a, #ff4a2a)', rarity: 4 },
  { id: 'palette-steel',     label: 'Steel',     gradient: 'linear-gradient(135deg,#c2ccd8 0%,#7d8896 50%,#3a4250 100%)',   rarity: 3 },
  { id: 'palette-arcane',    label: 'Arcane',    gradient: 'linear-gradient(135deg,#2a1a6a 0%,#6a4fd0 50%,#3ad6e6 100%)',   rarity: 3 },
  { id: 'palette-royal',     label: 'Royal',     gradient: 'linear-gradient(135deg,#3a1a55 0%,#7a3fa0 50%,#e8c24a 100%)',   rarity: 3 },
  { id: 'palette-naval',     label: 'Naval',     gradient: 'linear-gradient(135deg,#16305a 0%,#2a5a8a 50%,#d9a441 100%)',   rarity: 3 },
];

interface Props {
  value: IconValue;
  onChange: (next: IconValue) => void;
  inventory?: CosmeticInventory;
  onInspectLocked?: (item: LockedCosmeticInfo) => void;
  /** See module docblock. Default 'edit'. */
  variant?: 'create' | 'edit';
}

export interface LockedCosmeticInfo {
  kind: NonNullable<CosmeticInventory['unlockHints']>[number]['kind'];
  id: number | string;
  label: string;
  hint: string;
  progress?: { current: number; target: number; label: string };
}

export function IconPicker({ value, onChange, inventory, onInspectLocked, variant = 'edit' }: Props) {
  const create = variant === 'create';
  // Coerce legacy values into extended.
  const v: IconValue = {
    set: 'tavern-2',
    id: 0,
    hue: value.hue ?? 22,
    body: value.body ?? 0,
    hat: value.hat ?? 0,
    face: value.face ?? 0,
    accentHue: value.accentHue ?? ((value.hue ?? 22) + 60) % 360,
    ...(value.paletteId ? { paletteId: value.paletteId } : {}),
  };
  function update(patch: Partial<IconValue>): void {
    onChange({ ...v, ...patch });
  }
  const canBody = (i: number) => !inventory || inventory.bodies.includes(i);
  const canHat = (i: number) => !inventory || inventory.hats.includes(i);
  const canFace = (i: number) => !inventory || inventory.faces.includes(i);
  const canHue = (h: number) => !inventory || inventory.hues.includes(h);
  const canAccentHue = (h: number) => !inventory || inventory.accentHues.includes(h);
  const canPalette = (id: string) => !inventory || (inventory.palettes ?? []).includes(id);
  const hintFor = (kind: LockedCosmeticInfo['kind'], id: number | string, label: string): LockedCosmeticInfo => (
    inventory?.unlockHints?.find((h) => h.kind === kind && h.id === id) ?? { kind, id, label, hint: 'Unlock this through play.' }
  );
  const inspect = (kind: LockedCosmeticInfo['kind'], id: number | string, label: string) => {
    onInspectLocked?.(hintFor(kind, id, label));
  };

  // Chip-nav anchors (create variant).
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const faceRef = useRef<HTMLDivElement | null>(null);
  const hatRef = useRef<HTMLDivElement | null>(null);
  const colorsRef = useRef<HTMLDivElement | null>(null);
  const jump = (ref: React.MutableRefObject<HTMLDivElement | null>) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Locked splits for the create-variant teasers.
  const lockedShapes = BODY_SHAPES.map((b, i) => ({ b, i })).filter(({ i }) => !canBody(i));
  const lockedArchN = BODY_ARCHETYPES.filter((a) => !canBody(a.id)).length;
  const lockedHats = HAT_SHAPES.map((h, i) => ({ h, i })).filter(({ i }) => !canHat(i));
  const lockedFaces = FACE_SHAPES.map((f, i) => ({ f, i })).filter(({ i }) => !canFace(i));
  const lockedHueN = HUES.filter((h) => !canHue(h)).length;
  const lockedAccentN = HUES.filter((h) => !canAccentHue(h)).length;
  const lockedPaletteN = PALETTE_SWATCHES.filter((p) => !canPalette(p.id)).length;

  const bodySection = (
    <Section label="Body" anchorRef={bodyRef}>
      <div className="builder-grid builder-grid-tight">
        {BODY_SHAPES.map((b, i) => (create && !canBody(i)) ? null : (
          <BuilderTile
            key={b.id}
            selected={v.body === i}
            label={b.label}
            locked={!canBody(i)}
            onClick={() => canBody(i) ? update({ body: i }) : inspect('body', i, b.label)}
          >
            {/* 2D-phaseout (owner 2026-07-04): tiles show the SAME baked 3D
                character the stage shows — body variants full-figure. */}
            <Character3DThumbnail icon={{ ...v, body: i, hat: 0 }} size={48} frame="figure" alt={b.label} />
          </BuilderTile>
        ))}
        {create && BODY_ARCHETYPES.map((a) => !canBody(a.id) ? null : (
          <BuilderTile
            key={`arch-${a.id}`}
            selected={v.body === a.id}
            label={`${a.label} ${a.rarity === 4 ? '· Mythic' : '· Rare'}`}
            locked={false}
            onClick={() => update({ body: a.id })}
          >
            <ArchetypeSwatch a={a} />
          </BuilderTile>
        ))}
      </div>
      {!create && (
        /* Archetypes — Pixi-rendered. The picker can't draw the silhouette
           here, so each tile shows a colored placeholder with a Mythic/Rare
           label cue. The actual body draws correctly in-game once equipped. */
        <div className="builder-grid builder-grid-tight" style={{ marginTop: '0.5rem' }}>
          {BODY_ARCHETYPES.map((a) => (
            <BuilderTile
              key={`arch-${a.id}`}
              selected={v.body === a.id}
              label={`${a.label} ${a.rarity === 4 ? '· Mythic' : '· Rare'}`}
              locked={!canBody(a.id)}
              onClick={() => canBody(a.id) ? update({ body: a.id }) : inspect('body', a.id, a.label)}
            >
              <ArchetypeSwatch a={a} />
            </BuilderTile>
          ))}
        </div>
      )}
      {create && (lockedShapes.length + lockedArchN) > 0 && (
        <LockedTeaser count={lockedShapes.length + lockedArchN}>
          {lockedShapes.slice(0, 3).map(({ i }) => (
            <IconAvatar key={i} icon={{ ...v, body: i, hat: 0 }} size={30} />
          ))}
        </LockedTeaser>
      )}
    </Section>
  );

  const faceSection = (
    <Section label="Face" anchorRef={faceRef}>
      <div className="builder-grid builder-grid-tight">
        {FACE_SHAPES.map((f, i) => (create && !canFace(i)) ? null : (
          <BuilderTile
            key={f.id}
            selected={v.face === i}
            label={f.label}
            locked={!canFace(i)}
            onClick={() => canFace(i) ? update({ face: i }) : inspect('face', i, f.label)}
          >
            <Character3DThumbnail icon={{ ...v, face: i }} size={48} alt={f.label} />
          </BuilderTile>
        ))}
      </div>
      {create && lockedFaces.length > 0 && (
        <LockedTeaser count={lockedFaces.length}>
          {lockedFaces.slice(0, 3).map(({ i }) => (
            <IconAvatar key={i} icon={{ ...v, face: i }} size={30} />
          ))}
        </LockedTeaser>
      )}
    </Section>
  );

  const hatSection = (
    <Section label="Hat" anchorRef={hatRef}>
      <div className="builder-grid builder-grid-tight">
        {HAT_SHAPES.map((h, i) => (create && !canHat(i)) ? null : (
          <BuilderTile
            key={h.id}
            selected={v.hat === i}
            label={h.label}
            locked={!canHat(i)}
            onClick={() => canHat(i) ? update({ hat: i }) : inspect('hat', i, h.label)}
          >
            <Character3DThumbnail icon={{ ...v, hat: i }} size={48} alt={h.label} />
          </BuilderTile>
        ))}
      </div>
      {create && lockedHats.length > 0 && (
        <LockedTeaser count={lockedHats.length}>
          {lockedHats.slice(0, 3).map(({ i }) => (
            <IconAvatar key={i} icon={{ ...v, hat: i }} size={30} />
          ))}
        </LockedTeaser>
      )}
    </Section>
  );

  const paletteRow = (
    <div className="hue-row">
      <button
        type="button"
        className={`hue-cell hue-cell-off${!v.paletteId ? ' selected' : ''}`}
        onClick={() => {
          // Clear by emitting a copy without paletteId.
          const { paletteId: _drop, ...rest } = v;
          void _drop;
          onChange(rest);
        }}
        aria-label="No color set (use the swatches below)"
        title="None — mix your own"
      >
        <span style={{ fontSize: '0.7rem' }}>none</span>
      </button>
      {PALETTE_SWATCHES.map((p) => (create && !canPalette(p.id)) ? null : (
        <button
          key={p.id}
          type="button"
          className={`hue-cell${v.paletteId === p.id ? ' selected' : ''}${canPalette(p.id) ? '' : ' locked'}`}
          onClick={() => canPalette(p.id)
            ? update({ paletteId: p.id })
            : inspect('palette' as LockedCosmeticInfo['kind'], p.id, p.label)}
          style={{ background: p.gradient, position: 'relative' }}
          aria-label={canPalette(p.id) ? `${p.label} color set` : `${p.label} color set locked`}
          title={p.label}
        >
          {p.rarity === 4 && (
            <span style={{
              position: 'absolute', bottom: 1, right: 2, fontSize: 8,
              color: '#ffd770', textShadow: '0 0 2px #000',
            }}>★</span>
          )}
        </button>
      ))}
      {create && lockedPaletteN > 0 && <MoreChip count={lockedPaletteN} />}
    </div>
  );

  const hueRow = (which: 'hue' | 'accentHue') => {
    const can = which === 'hue' ? canHue : canAccentHue;
    const cur = which === 'hue' ? v.hue : v.accentHue;
    const lockedN = which === 'hue' ? lockedHueN : lockedAccentN;
    const sat = which === 'hue' ? '70% 56%' : '75% 60%';
    return (
      <div className="hue-row">
        {HUES.map((hue) => (create && !can(hue)) ? null : (
          <button
            key={`${which}-${hue}`}
            type="button"
            className={`hue-cell${cur === hue ? ' selected' : ''}${can(hue) ? '' : ' locked'}`}
            onClick={() => can(hue)
              ? update(which === 'hue' ? { hue } : { accentHue: hue })
              : inspect(which, hue, `${which === 'hue' ? 'Hue' : 'Accent hue'} ${hue}`)}
            style={{ background: `hsl(${hue} ${sat})` }}
            aria-label={can(hue) ? `${which === 'hue' ? 'body' : 'accent'} hue ${hue}` : `${which === 'hue' ? 'body' : 'accent'} hue ${hue} locked`}
          />
        ))}
        {create && lockedN > 0 && <MoreChip count={lockedN} />}
      </div>
    );
  };

  if (create) {
    return (
      <div className="builder">
        <div className="builder-chips" role="tablist" aria-label="Builder sections">
          <button type="button" className="builder-chip" onClick={() => jump(bodyRef)}>Body</button>
          <button type="button" className="builder-chip" onClick={() => jump(faceRef)}>Face</button>
          <button type="button" className="builder-chip" onClick={() => jump(hatRef)}>Hat</button>
          <button type="button" className="builder-chip" onClick={() => jump(colorsRef)}>Colors</button>
        </div>
        {bodySection}
        {faceSection}
        {hatSection}
        <Section label="Color sets" anchorRef={colorsRef}>
          <p className="muted" style={{ margin: '0 0 0.4rem', fontSize: '0.78rem' }}>
            A matched trio — or mix your own below.
          </p>
          {paletteRow}
        </Section>
        <Section label="Color">{hueRow('hue')}</Section>
        <Section label="Accent">{hueRow('accentHue')}</Section>
      </div>
    );
  }

  return (
    <div className="builder">
      {bodySection}
      <Section label="Color sets">
        <p className="muted" style={{ margin: '0 0 0.4rem', fontSize: '0.78rem' }}>
          A matched trio — overrides the Color and Accent swatches. "None" mixes your own.
        </p>
        {paletteRow}
      </Section>
      {hatSection}
      {faceSection}
      <Section label="Color">{hueRow('hue')}</Section>
      <Section label="Accent">{hueRow('accentHue')}</Section>
    </div>
  );
}

function ArchetypeSwatch({ a }: { a: { color: string; rarity: 3 | 4 } }) {
  return (
    <span style={{
      display: 'inline-block',
      width: 48, height: 48,
      borderRadius: '50%',
      background: a.color,
      border: a.rarity === 4 ? '2px solid #ffd770' : '2px solid #4a9eff',
      boxShadow: a.rarity === 4 ? '0 0 8px rgba(255,215,112,0.4)' : 'none',
    }} aria-hidden="true" />
  );
}

function Section({ label, children, anchorRef }: {
  label: string; children: React.ReactNode;
  anchorRef?: React.MutableRefObject<HTMLDivElement | null>;
}) {
  return (
    <div className="builder-section" {...(anchorRef ? { ref: anchorRef } : {})}>
      <div className="builder-label muted">{label}</div>
      {children}
    </div>
  );
}

/** One quiet line standing in for a section's locked items (create
 *  variant): a few greyed silhouettes + the honest count. Decorative —
 *  earning happens at the tables, not here. */
function LockedTeaser({ count, children }: { count: number; children?: React.ReactNode }) {
  return (
    <div className="locked-teaser" aria-hidden="true">
      {children && <span className="locked-teaser-sils">{children}</span>}
      <span className="locked-teaser-text">+{count} more — earn them at the tables</span>
    </div>
  );
}

/** Compact "+N" tail chip for swatch rows in the create variant. */
function MoreChip({ count }: { count: number }) {
  return (
    <span className="hue-cell hue-more" aria-hidden="true">+{count}</span>
  );
}

function BuilderTile({ selected, locked = false, label, onClick, children }: {
  selected: boolean; locked?: boolean; label: string; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={`builder-cell${selected ? ' selected' : ''}${locked ? ' locked' : ''}`}
      onClick={onClick}
      aria-label={label}
      title={locked ? `${label} locked - click to see how to unlock` : label}
    >
      {children}
      {locked && <span className="lock-mark">Locked</span>}
    </button>
  );
}
